'use client';

import type React from 'react';
import { useRef } from 'react';
import type { TenantContact } from 'src/types';

interface TenantSidebarProps {
	contacts: TenantContact[];
	activeIndex: number;
	onSelect: (index: number) => void;
}

const TenantSidebar: React.FC<TenantSidebarProps> = ({
	contacts,
	activeIndex,
	onSelect,
}) => {
	const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

	const focusItem = (index: number) => {
		onSelect(index);
		itemRefs.current[index]?.focus();
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		const lastIndex = contacts.length - 1;
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				focusItem(activeIndex >= lastIndex ? 0 : activeIndex + 1);
				break;
			case 'ArrowUp':
				event.preventDefault();
				focusItem(activeIndex <= 0 ? lastIndex : activeIndex - 1);
				break;
			case 'Home':
				event.preventDefault();
				focusItem(0);
				break;
			case 'End':
				event.preventDefault();
				focusItem(lastIndex);
				break;
			default:
				break;
		}
	};

	return (
		<nav
			className="tenant-texts__sidebar"
			aria-label="Select tenant"
			onKeyDown={handleKeyDown}
		>
			<ul className="tenant-texts__sidebar-list">
				{contacts.map((contact, index) => {
					const isActive = index === activeIndex;
					return (
						<li
							key={contact.tenant_contact_id}
							className="tenant-texts__sidebar-list-item"
						>
							<button
								type="button"
								ref={(el) => {
									itemRefs.current[index] = el;
								}}
								tabIndex={isActive ? 0 : -1}
								aria-pressed={isActive}
								className={[
									'tenant-texts__sidebar-item',
									isActive ? 'tenant-texts__sidebar-item--active' : '',
								]
									.filter(Boolean)
									.join(' ')}
								onClick={() => onSelect(index)}
							>
								{contact.name}
							</button>
						</li>
					);
				})}
			</ul>
		</nav>
	);
};

export default TenantSidebar;
